import { useEffect, useRef } from 'react';
import { useSimulatorStore } from '../stores/useSimulatorStore';
import { apiFetch } from '../lib/api';
import { getSessionToken } from '../lib/auth';

let hydrated = false;

export function useWorkspacePersistence() {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextSave = useRef(true);

  // Hydrate from server on mount (only if authenticated)
  useEffect(() => {
    if (hydrated) return;
    if (!getSessionToken()) return;
    hydrated = true;

    apiFetch('/api/workspace')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          useSimulatorStore.getState().hydrateFromServer(data);
        }
        skipNextSave.current = false;
      })
      .catch(() => {
        skipNextSave.current = false;
      });
  }, []);

  // Subscribe to state changes and auto-save (debounced, only if authenticated)
  useEffect(() => {
    if (!getSessionToken()) return;

    const unsub = useSimulatorStore.subscribe((state, prev) => {
      if (skipNextSave.current) return;
      if (!getSessionToken()) return;

      const changed =
        state.assumptions !== prev.assumptions ||
        state.packages !== prev.packages ||
        state.selectedPrice !== prev.selectedPrice ||
        state.activeTab !== prev.activeTab ||
        state.insightCache !== prev.insightCache ||
        state.chatMessages !== prev.chatMessages ||
        state.marketingSelections !== prev.marketingSelections;

      if (!changed) return;

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        apiFetch('/api/workspace', {
          method: 'PUT',
          body: JSON.stringify({
            assumptions: state.assumptions,
            packages: state.packages,
            selectedPrice: state.selectedPrice,
            activeTab: state.activeTab,
            insightCache: state.insightCache,
            chatMessages: state.chatMessages,
            marketingSelections: state.marketingSelections,
          }),
        }).catch(() => {});
      }, 800);
    });

    return () => {
      unsub();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
}

export function resetHydration() {
  hydrated = false;
}
